import React, { useEffect, useState, useRef } from 'react';
import { 
  Container, Typography, Box, Divider, Grid, Card, CardActionArea, CardMedia, CardContent, 
  Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip, 
  Fade, Grow, TextField, Modal, Backdrop, useMediaQuery, Stack, Tooltip, Alert 
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import SpaIcon from '@mui/icons-material/Spa';
import config from './config';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals } from './currencyHelpers';
import { fetchProducts, fetchCategories } from './api';
import ClosedMessage from './ClosedMessage';
import { isBranchOpen } from './branchUtils';

function shouldDisplayDimensionInitial(val) {
  if (typeof val === 'number') return !isNaN(val) && val !== 0;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    return trimmed.length > 0 && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined' && trimmed !== '0';
  }
  return false;
}

const getImageUrl = (imageData, size = 'medium') => {
  const fallbackUrl = '/logo-storesye.png';
  if (!imageData) return fallbackUrl;
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (size === 'large' && imageData.large) {
      return imageData.large.startsWith('http') ? imageData.large : `${config.SITE_BASE_URL}${imageData.large}`;
    }
    if (imageData.medium) return imageData.medium.startsWith('http') ? imageData.medium : `${config.SITE_BASE_URL}${imageData.medium}`;
    return fallbackUrl;
  }
  if (typeof imageData === 'string') {
    if (imageData.startsWith('/images/')) return `${config.SITE_BASE_URL}${imageData}`;
    if (imageData.startsWith('data:image') || imageData.startsWith('http')) return imageData;
  }
  return fallbackUrl;
};

const getAllCustomProductImages = (prod) => {
  const imgs = [];
  const mainImageUrl = getImageUrl(prod.ImageUrl || prod.ImageJson, 'large');
  imgs.push(mainImageUrl);

  if (prod.ProductImages && Array.isArray(prod.ProductImages) && prod.ProductImages.length > 0) {
    prod.ProductImages.forEach((img) => {
      let imgUrl = null;
      if (typeof img === 'object' && img !== null) {
        imgUrl = img.large || img.medium || img.thumbnail;
      } else if (typeof img === 'string') {
        imgUrl = img;
      }
      if (imgUrl) {
        const fullUrl = imgUrl.startsWith('http') ? imgUrl : `${config.SITE_BASE_URL}${imgUrl}`;
        imgs.push(fullUrl);
      }
    });
  }
  return imgs;
};

export default function CustomProducts() {
  const { selectedBranch, cartItems = [] } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  
  const [branchStatus, setBranchStatus] = useState({ isOpen: true, nextOpen: '' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateProductName, setDuplicateProductName] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const scrollRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedSubProducts, setSelectedSubProducts] = useState([]);
  const [customTotal, setCustomTotal] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const autoSlideIntervalRef = useRef(null);

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);

  const isProductInCart = (productId) => cartItems.some(item => item.Id === productId && item.isCustom);

  // ✅ Open external product URL helper
  const handleOpenExternalProduct = (prod, e) => {
    if (e) e.stopPropagation();
    const productId = prod.Id || prod.id || prod.ProductId;
    const url = `https://www.elorayem.com/product/${productId}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setTimeout(() => {
      const scrollAmount = Math.floor(window.innerHeight * 0.1);
      let start = window.scrollY, end = scrollAmount, duration = 1200, startTime = null;
      function animateScroll(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime, progress = Math.min(elapsed / duration, 1);
        const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
        window.scrollTo(0, start + (end - start) * ease);
        if (progress < 1) window.requestAnimationFrame(animateScroll);
      }
      window.requestAnimationFrame(animateScroll);
    }, 400);
  }, []);

  useEffect(() => {
    if (selectedBranch) setBranchStatus(isBranchOpen(selectedBranch));
  }, [selectedBranch]);

  const handleImageClick = (imageData, e) => {
    e.stopPropagation();
    let largeUrl = null;
    if (imageData && typeof imageData === 'object' && !Array.isArray(imageData) && imageData.large) {
      largeUrl = imageData.large.startsWith('http') ? imageData.large : `${config.SITE_BASE_URL}${imageData.large}`;
    }
    setSelectedImage(largeUrl || getImageUrl(imageData));
  };

  const handleCloseModal = () => setSelectedImage(null);

  useEffect(() => {
    fetchProducts(selectedBranch?.id, selectedCurrency).then(data => {
      setProducts(data.filter(p => Array.isArray(p.SubProducts) && p.SubProducts.length > 0));
    });
    fetchCategories(selectedBranch?.id).then(cats => {
      const filtered = cats.filter(cat => !/^(\u0627\u0644\u0639\u0631\u0648\u0636:|offer|deal|\u0645\u062c\u0645\u0648\u0639\u0629:)/i.test(cat.CategoryName));
      filtered.sort((a, b) => (a.CategoriesOrder ?? 9999) - (b.CategoriesOrder ?? 9999));
      setCategories(filtered);
      if (filtered.length > 0) setActiveCategory(filtered[0].Id);
    });
  }, [selectedBranch, selectedCurrency]);

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    const el = document.getElementById(`custom-category-section-${catId}`);
    if (el) window.scrollTo({ top: el.offsetTop - 180, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      const viewportCenter = window.innerHeight / 2;
      let found = null;
      for (const cat of categories) {
        const el = document.getElementById(`custom-category-section-${cat.Id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= viewportCenter && rect.bottom > viewportCenter) {
            found = cat.Id;
            break;
          }
        }
      }
      if (found && activeCategory !== found) setActiveCategory(found);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories, activeCategory]);

  useEffect(() => {
    if (!activeCategory) return;
    const scrollEl = scrollRef.current, catEl = document.getElementById(`cat-btn-${activeCategory}`);
    if (scrollEl && catEl) {
      const scrollAmount = catEl.offsetLeft - scrollEl.offsetWidth / 2 + catEl.offsetWidth / 2;
      scrollEl.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [activeCategory]);

  // Auto-slide effect
  useEffect(() => {
    if (customizeOpen && selectedProduct && isAutoSliding) {
      const imgs = getAllCustomProductImages(selectedProduct);
      if (imgs.length > 1) {
        autoSlideIntervalRef.current = setInterval(() => {
          setCurrentImageIndex(prev => (prev + 1) % imgs.length);
        }, 3000);
      }
      return () => { if (autoSlideIntervalRef.current) clearInterval(autoSlideIntervalRef.current); };
    }
  }, [customizeOpen, selectedProduct, isAutoSliding]);

  const handleNextImage = () => {
    if (!selectedProduct) return;
    const imgs = getAllCustomProductImages(selectedProduct);
    setCurrentImageIndex(prev => (prev + 1) % imgs.length);
    if (isAutoSliding && autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);
      autoSlideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % imgs.length);
      }, 3000);
    }
  };

  const handlePrevImage = () => {
    if (!selectedProduct) return;
    const imgs = getAllCustomProductImages(selectedProduct);
    setCurrentImageIndex(prev => (prev - 1 + imgs.length) % imgs.length);
    if (isAutoSliding && autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);
      autoSlideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % imgs.length);
      }, 3000);
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    if (isAutoSliding && autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);
      if (selectedProduct) {
        const imgs = getAllCustomProductImages(selectedProduct);
        autoSlideIntervalRef.current = setInterval(() => {
          setCurrentImageIndex(prev => (prev + 1) % imgs.length);
        }, 3000);
      }
    }
  };

  const toggleAutoSlide = () => setIsAutoSliding(prev => !prev);

  const handleCustomize = (product) => {
    setSelectedProduct(product);

    const filteredSubs = product.SubProducts.filter(sp => sp.IsActive !== false);
    setSelectedSubProducts(filteredSubs.map(sp => ({
      ...sp,
      selected: false,
      Height: sp.Height ?? '',
      Width: sp.Width ?? '',
      Quantity: sp.FixedQuantity ? 1 : (sp.Quantity || 0),
      showHeightInitial: shouldDisplayDimensionInitial(sp.Height),
      showWidthInitial: shouldDisplayDimensionInitial(sp.Width)
    })));

    const productColors = product.ProductColors || [];
    if (productColors.length === 1) {
      setSelectedColor(productColors[0]);
    } else {
      setSelectedColor(null);
    }

    const productSizes = product.ProductSizes || [];
    if (productSizes.length === 1) {
      setSelectedSize(productSizes[0]);
    } else {
      setSelectedSize(null);
    }

    setCurrentImageIndex(0);
    setIsAutoSliding(true);
    setCustomizeOpen(true);

    const isCpActive =
      product.CPAccountActive === true ||
      product.CPAccountActive === 1 ||
      product.CPAccountActive === 'true' ||
      product.CPAccountActive === '1';

    setCustomTotal(isCpActive ? 0 : getPriceInCurrency(product, selectedCurrency));
  };

  const recalculateTotal = (subs) => {
    const isCpActive =
      selectedProduct?.CPAccountActive === true ||
      selectedProduct?.CPAccountActive === 1 ||
      selectedProduct?.CPAccountActive === 'true' ||
      selectedProduct?.CPAccountActive === '1';

    const mainProductPrice = !isCpActive && selectedProduct
      ? getPriceInCurrency(selectedProduct, selectedCurrency)
      : 0;

    let total = mainProductPrice;

    subs.forEach(sp => {
      if (sp.selected) {
        const qty = Number(sp.Quantity || 0) || 0;
        const spPrice = getPriceInCurrency(sp, selectedCurrency);
        total += spPrice * qty;
      }
    });

    setCustomTotal(total);
  };

  const handleCloseCustomize = () => {
    setCustomizeOpen(false);
    setSelectedColor(null);
    setSelectedSize(null);
    if (autoSlideIntervalRef.current) clearInterval(autoSlideIntervalRef.current);
    setIsAutoSliding(false);
    setCurrentImageIndex(0);
  };

  const handleSubProductToggle = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        const nextSelected = !sp.selected;
        if (sp.FixedQuantity) return { ...sp, selected: nextSelected, Quantity: nextSelected ? 1 : 0 };
        return { ...sp, selected: nextSelected, Quantity: nextSelected ? (sp.Quantity && sp.Quantity > 0 ? sp.Quantity : 1) : 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleIncrement = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        if (sp.FixedQuantity) return sp;
        const qty = Number(sp.Quantity || 0) + 1;
        return { ...sp, Quantity: qty, selected: qty > 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleDecrement = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        if (sp.FixedQuantity) return sp;
        const qty = Math.max(0, Number(sp.Quantity || 0) - 1);
        return { ...sp, Quantity: qty, selected: qty > 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleSubProductFieldChange = (idx, field, value) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        if (field === 'Quantity' && sp.FixedQuantity) return sp;
        return { ...sp, [field]: value };
      });
      if (field === 'Quantity') {
        updated.forEach((u, i) => { if (i === idx) u.selected = Number(u.Quantity || 0) > 0; });
        recalculateTotal(updated);
      }
      return updated;
    });
  };

  if (!branchStatus.isOpen) return <ClosedMessage openTime={branchStatus.nextOpen} />;

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: { xs: 3, sm: 4 }, mb: 20, px: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 4, sm: 5, md: 6 }, textAlign: 'center', position: 'relative' }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, 
            color: theme.palette.primary.main, 
            letterSpacing: '0.5px', 
            display: 'inline-block', 
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent', 
            backgroundClip: 'text', 
            position: 'relative', 
            '&::after': { 
              content: '""', 
              position: 'absolute', 
              bottom: { xs: -8, sm: -10, md: -12 }, 
              left: '50%', 
              transform: 'translateX(-50%)', 
              width: { xs: '60px', sm: '70px', md: '80px' }, 
              height: { xs: '3px', sm: '3.5px', md: '4px' }, 
              borderRadius: '2px', 
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`, 
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}` 
            } 
          }}>
            {t('customProducts.title', 'Customizable Products')}
          </Typography>
        </Box>

        {/* Category Navigation */}
        {categories.length > 0 && (
          <Box ref={scrollRef} sx={{ 
            position: 'sticky', top: { xs: 80, sm: 90, md: 100 }, zIndex: 100, display: 'flex', overflowX: 'auto', 
            py: { xs: 1.5, sm: 2 }, mb: { xs: 4, sm: 5, md: 6 }, px: { xs: 1.5, sm: 2 }, 
            bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(10px)', 
            borderRadius: { xs: '16px', sm: '18px', md: '20px' }, 
            boxShadow: `0 ${isMobile ? '4px 16px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, gap: { xs: 1.5, sm: 2 }, 
            scrollbarWidth: 'thin', 
            '&::-webkit-scrollbar': { height: { xs: 6, sm: 8 } }, 
            '&::-webkit-scrollbar-track': { bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: '10px' }, 
            '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.primary.main, 0.3), borderRadius: '10px', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.5) } } 
          }}>
            {categories.filter(cat => products.some(p => p.CategoryId === cat.Id)).map(cat => (
              <Button 
                key={cat.Id} id={`cat-btn-${cat.Id}`} onClick={() => handleCategoryClick(cat.Id)} 
                variant={activeCategory === cat.Id ? 'contained' : 'outlined'} size={isMobile ? "small" : "medium"}
                sx={{ 
                  fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' }, 
                  borderRadius: { xs: '12px', sm: '14px', md: '16px' }, px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 0.8, sm: 1, md: 1.2 }, 
                  minWidth: 'fit-content', whiteSpace: 'nowrap', 
                  bgcolor: activeCategory === cat.Id ? theme.palette.primary.main : 'transparent', 
                  color: activeCategory === cat.Id ? theme.palette.primary.contrastText : theme.palette.primary.main, 
                  borderColor: activeCategory === cat.Id ? 'transparent' : alpha(theme.palette.primary.main, 0.3), 
                  boxShadow: activeCategory === cat.Id ? `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}` : 'none', 
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                  '&:hover': { bgcolor: activeCategory === cat.Id ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08), borderColor: theme.palette.primary.main, transform: isMobile ? 'none' : 'translateY(-2px)', boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.25)}` },
                  '&:active': { transform: isMobile ? 'scale(0.98)' : 'translateY(-2px)' }
                }}
              >
                {cat.CategoryName}
              </Button>
            ))}
          </Box>
        )}

        {/* Product Categories */}
        {categories.map((cat, catIndex) => {
          const catProducts = products.filter(prod => prod.CategoryId === cat.Id);
          if (catProducts.length === 0) return null;
          
          return (
          <Box key={cat.Id} id={`custom-category-section-${cat.Id}`} sx={{ 
            mb: { xs: 8, sm: 9, md: 10 }, animation: 'fadeInUp 0.6s ease-out', animationDelay: `${catIndex * 0.1}s`, animationFillMode: 'backwards', 
            '@keyframes fadeInUp': { from: { opacity: 0, transform: 'translateY(30px)' }, to: { opacity: 1, transform: 'translateY(0)' } } 
          }}>
            <Box sx={{ mb: { xs: 3, sm: 3.5, md: 4 }, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' }, color: theme.palette.primary.main, 
                display: 'inline-block', background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, 
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', position: 'relative', 
                '&::after': { content: '""', position: 'absolute', bottom: { xs: -6, sm: -7, md: -8 }, left: '50%', transform: 'translateX(-50%)', width: { xs: '45px', sm: '52px', md: '60px' }, height: { xs: '3px', sm: '3.5px', md: '4px' }, borderRadius: '2px', background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`, boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}` } 
              }}>
                {cat.CategoryName}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} justifyContent="center">
              {products.filter(prod => prod.CategoryId === cat.Id).map((prod, prodIndex) => {
                const imageUrl = getImageUrl(prod.ImageUrl);
                const hasColors = prod.ProductColors && Array.isArray(prod.ProductColors) && prod.ProductColors.length > 0;
                const productImagesCount = prod.ProductImages && Array.isArray(prod.ProductImages) ? prod.ProductImages.length : 0;
                
                return (
                  <Grid item key={prod.Id} sx={{ 
                    animation: 'fadeInScale 0.5s ease-out', animationDelay: `${prodIndex * 0.05}s`, animationFillMode: 'backwards', 
                    '@keyframes fadeInScale': { from: { opacity: 0, transform: 'scale(0.9)' }, to: { opacity: 1, transform: 'scale(1)' } } 
                  }}>
                    <Card sx={{ 
                      width: { xs: 160, sm: 240, md: 300 }, minHeight: { xs: 300, sm: 380, md: 420 }, 
                      borderRadius: { xs: '18px', sm: '20px', md: '24px' }, overflow: 'hidden', 
                      boxShadow: `0 ${isMobile ? '4px 16px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`, 
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: 'background.paper', 
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                      '&:hover': { transform: isMobile ? 'scale(0.98)' : 'translateY(-12px)', boxShadow: `0 ${isMobile ? '8px 20px' : '20px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, '& .product-image': { transform: 'scale(1.08)' }, '& .hover-overlay': { opacity: isMobile ? 0 : 1 } }, 
                      '&:active': { transform: isMobile ? 'scale(0.95)' : 'none' }, position: 'relative' 
                    }}>
                      <CardActionArea sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleCustomize(prod); }}>
                        <Box sx={{ position: 'relative', overflow: 'hidden', height: { xs: 120, sm: 160, md: 200 }, cursor: 'zoom-in' }} onClick={(e) => handleImageClick(prod.ImageUrl, e)}>
                          <CardMedia component="img" image={imageUrl} alt={prod.ProductName} className="product-image" sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                          
                          {!isMobile && (
                            <Box className="hover-overlay" sx={{ 
                              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                              background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.6)}, transparent)`, 
                              opacity: 0, transition: 'opacity 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' 
                            }}>
                              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Click to Zoom</Typography>
                            </Box>
                          )}
                          
                          <Chip 
                            icon={<TuneIcon sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }} />} 
                            label={t('customizable', 'Customizable')} 
                            sx={{ 
                              position: 'absolute', top: { xs: 8, sm: 10, md: 12 }, right: { xs: 8, sm: 10, md: 12 }, 
                              bgcolor: alpha(theme.palette.primary.main, 0.95), color: theme.palette.primary.contrastText, 
                              fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, 
                              height: { xs: 22, sm: 24, md: 28 }, borderRadius: { xs: '11px', sm: '12px', md: '14px' }, 
                              backdropFilter: 'blur(10px)', boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.4)}`, zIndex: 1 
                            }} 
                          />

                          {/* Gallery badge */}
                          {productImagesCount > 0 && (
                            <Chip 
                              icon={<PhotoLibraryIcon sx={{ fontSize: { xs: 10, sm: 12 }, color: 'white !important' }} />}
                              label={`+${productImagesCount}`}
                              sx={{ 
                                position: 'absolute', bottom: { xs: 8, sm: 10, md: 12 }, left: { xs: 8, sm: 10, md: 12 }, 
                                bgcolor: 'rgba(0,0,0,0.65)', color: 'white', fontWeight: 700, 
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }, 
                                height: { xs: 20, sm: 22, md: 26 }, borderRadius: { xs: '10px', sm: '11px', md: '13px' }, 
                                zIndex: 1, '& .MuiChip-icon': { color: 'white' }
                              }} 
                            />
                          )}

                          {/* Colors Badge */}
                          {hasColors && (
                            <Chip 
                              icon={<PaletteIcon sx={{ fontSize: { xs: 10, sm: 12 }, color: 'white !important' }} />}
                              label={`${prod.ProductColors.length} ${t('colors', 'Colors')}`}
                              sx={{ 
                                position: 'absolute', bottom: { xs: 8, sm: 10, md: 12 }, right: { xs: 8, sm: 10, md: 12 }, 
                                bgcolor: alpha(theme.palette.secondary.main, 0.95), color: 'white', fontWeight: 700, 
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }, 
                                height: { xs: 20, sm: 22, md: 26 }, borderRadius: { xs: '10px', sm: '11px', md: '13px' }, 
                                backdropFilter: 'blur(10px)', boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.secondary.main, 0.4)}`, 
                                zIndex: 1, '& .MuiChip-icon': { color: 'white' }
                              }} 
                            />
                          )}

                          {prod.ProductSizes && Array.isArray(prod.ProductSizes) && prod.ProductSizes.length > 0 && (
                            <Chip 
                              icon={<span style={{ fontSize: 10, color: 'white' }}>📏</span>}
                              label={`${prod.ProductSizes.length} ${t('sizes', 'Sizes')}`}
                              sx={{ 
                                position: 'absolute', 
                                top: { xs: 38, sm: 44, md: 50 },
                                right: { xs: 8, sm: 10, md: 12 }, 
                                bgcolor: '#ed6c02', color: 'white', fontWeight: 700, 
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }, 
                                height: { xs: 20, sm: 22, md: 26 }, 
                                borderRadius: { xs: '10px', sm: '11px', md: '13px' }, 
                                boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha('#ed6c02', 0.4)}`, 
                                zIndex: 1
                              }} 
                            />
                          )}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2, md: 2.5 }, width: '100%' }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, 
                            mb: { xs: 1, sm: 1.2, md: 1.5 }, textAlign: 'center', color: theme.palette.text.primary, lineHeight: 1.3,
                            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                          }}>
                            {prod.ProductName}
                          </Typography>
                          
                          {prod.Description && (
                            <Typography variant="body2" sx={{ 
                              color: theme.palette.text.secondary, mb: { xs: 2, sm: 2.5, md: 3 }, textAlign: 'center', lineHeight: 1.5, 
                              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                              overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                            }}>
                              {prod.Description}
                            </Typography>
                          )}
                          
                          {/* Action Button: تسوق الان opening https://www.elorayem.com/product/{PRODUCT ID} in new tab */}
                          <Button 
                            variant="contained" fullWidth onClick={(e) => handleOpenExternalProduct(prod, e)} 
                            size={isMobile ? "small" : "medium"}
                            sx={{ 
                              mt: 'auto', py: { xs: 1, sm: 1.2, md: 1.5 }, fontWeight: 700, 
                              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, borderRadius: { xs: '12px', sm: '14px', md: '16px' }, 
                              bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, 
                              boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`, textTransform: 'none', 
                              '&:hover': { bgcolor: theme.palette.primary.dark, transform: isMobile ? 'none' : 'translateY(-2px)' }, 
                              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' },
                              transition: 'all 0.3s ease', gap: { xs: 0.5, sm: 0.8, md: 1 } 
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                            تسوق الان
                          </Button>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          );
        })}
      </Container>

      {/* Customization Dialog */}
      <Dialog 
        open={customizeOpen} 
        onClose={handleCloseCustomize} 
        maxWidth="lg" 
        fullWidth 
        fullScreen={false}
        PaperProps={{ sx: { 
          borderRadius: isMobile ? '20px 20px 0 0' : '24px', 
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          bgcolor: 'background.paper',
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            margin: 0,
            width: '100%',
            maxWidth: '100%',
          })
        } }} 
        TransitionComponent={Fade} 
        transitionDuration={400}
        sx={isMobile ? {
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
          '& .MuiDialog-paper': {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
          }
        } : {}}
      >
        <DialogTitle sx={{ 
          fontWeight: 800, fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.75rem' }, color: theme.palette.primary.main, 
          pb: { xs: 1.5, sm: 2 }, pt: { xs: 2, sm: 2.5, md: 3 }, px: { xs: 2, sm: 2.5, md: 3 }, 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}` 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <TuneIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 }, color: theme.palette.primary.main }} />
            <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {selectedProduct?.ProductName || t('customizeProduct', 'Customize Your Product')}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCustomize} size={isMobile ? "small" : "medium"}
            sx={{ color: theme.palette.text.secondary, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main } }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mt: { xs: 1, sm: 1.5, md: 2 } }}>
          {selectedProduct && (
            <>
              {/* Image Gallery */}
              {(() => {
                const modalImages = getAllCustomProductImages(selectedProduct);
                const hasMultipleImages = modalImages.length > 1;

                return (
                  <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: { xs: 250, sm: 350, md: 420 },
                        borderRadius: { xs: '16px', sm: '20px', md: '24px' },
                        overflow: 'hidden',
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        cursor: 'zoom-in',
                        '&:hover .gallery-nav': { opacity: 1 },
                        '&:hover .zoom-hint': { opacity: 1 }
                      }}
                      onClick={() => setSelectedImage(modalImages[currentImageIndex])}
                    >
                      <Box
                        component="img"
                        src={modalImages[currentImageIndex]}
                        alt={selectedProduct.ProductName}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          transition: 'opacity 0.4s ease',
                        }}
                      />

                      {hasMultipleImages && (
                        <>
                          <IconButton
                            className="gallery-nav"
                            onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                            sx={{
                              position: 'absolute', left: { xs: 8, sm: 12 }, top: '50%', transform: 'translateY(-50%)',
                              bgcolor: 'rgba(255,255,255,0.92)', opacity: isMobile ? 1 : 0,
                              transition: 'all 0.3s ease', width: { xs: 36, sm: 42, md: 48 }, height: { xs: 36, sm: 42, md: 48 },
                              boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
                              '&:hover': { bgcolor: 'white', transform: 'translateY(-50%) scale(1.1)' }
                            }}
                          >
                            <NavigateBeforeIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
                          </IconButton>
                          <IconButton
                            className="gallery-nav"
                            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                            sx={{
                              position: 'absolute', right: { xs: 8, sm: 12 }, top: '50%', transform: 'translateY(-50%)',
                              bgcolor: 'rgba(255,255,255,0.92)', opacity: isMobile ? 1 : 0,
                              transition: 'all 0.3s ease', width: { xs: 36, sm: 42, md: 48 }, height: { xs: 36, sm: 42, md: 48 },
                              boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
                              '&:hover': { bgcolor: 'white', transform: 'translateY(-50%) scale(1.1)' }
                            }}
                          >
                            <NavigateNextIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
                          </IconButton>
                        </>
                      )}

                      {hasMultipleImages && (
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); toggleAutoSlide(); }}
                          sx={{
                            position: 'absolute', bottom: { xs: 10, sm: 14 }, left: { xs: 10, sm: 14 },
                            bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                            width: { xs: 32, sm: 36, md: 40 }, height: { xs: 32, sm: 36, md: 40 },
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                          }}
                        >
                          {isAutoSliding ? <PauseIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} /> : <PlayArrowIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />}
                        </IconButton>
                      )}

                      {hasMultipleImages && (
                        <Box sx={{
                          position: 'absolute', top: { xs: 10, sm: 14 }, right: { xs: 10, sm: 14 },
                          bgcolor: 'rgba(0,0,0,0.5)', color: 'white', borderRadius: '12px',
                          px: { xs: 1, sm: 1.5 }, py: { xs: 0.3, sm: 0.5 },
                          fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 700
                        }}>
                          {currentImageIndex + 1}/{modalImages.length}
                        </Box>
                      )}

                      <Box
                        className="zoom-hint"
                        sx={{
                          position: 'absolute', bottom: { xs: 10, sm: 14 }, right: { xs: 10, sm: 14 },
                          bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '50%',
                          width: { xs: 32, sm: 36, md: 40 }, height: { xs: 32, sm: 36, md: 40 },
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: isMobile ? 1 : 0, transition: 'opacity 0.3s ease'
                        }}
                      >
                        <ZoomInIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, color: 'white' }} />
                      </Box>
                    </Box>

                    {hasMultipleImages && (
                      <Box sx={{
                        display: 'flex', gap: { xs: 1, sm: 1.5 }, mt: { xs: 1.5, sm: 2 },
                        overflowX: 'auto', pb: 1,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-track': { bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: '10px' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.primary.main, 0.2), borderRadius: '10px' }
                      }}>
                        {modalImages.map((img, index) => (
                          <Box
                            key={`thumb-${index}`}
                            onClick={() => handleThumbnailClick(index)}
                            sx={{
                              width: { xs: 56, sm: 68, md: 80 }, height: { xs: 56, sm: 68, md: 80 },
                              minWidth: { xs: 56, sm: 68, md: 80 },
                              borderRadius: { xs: '10px', sm: '12px' },
                              overflow: 'hidden', cursor: 'pointer',
                              border: `3px solid ${currentImageIndex === index ? theme.palette.primary.main : 'transparent'}`,
                              opacity: currentImageIndex === index ? 1 : 0.6,
                              transition: 'all 0.3s ease',
                              boxShadow: currentImageIndex === index ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                              '&:hover': { opacity: 1, transform: 'scale(1.05)', borderColor: alpha(theme.palette.primary.main, 0.5) }
                            }}
                          >
                            <Box component="img" src={img} alt={`Thumbnail ${index + 1}`}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}

                    {selectedProduct.Description && (
                      <Box sx={{ mt: { xs: 2, sm: 2.5 } }}>
                        <Typography variant="body1" sx={{
                          color: theme.palette.text.secondary, lineHeight: 1.7,
                          fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
                        }}>
                          {selectedProduct.Description}
                        </Typography>
                      </Box>
                    )}

                    {selectedProduct.Ingredients && (
                      <Box sx={{
                        mt: { xs: 2, sm: 2.5 }, p: { xs: 1.5, sm: 2 },
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderRadius: { xs: '12px', sm: '14px' },
                        border: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`
                      }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <SpaIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                            {t('products.ingredients', 'Ingredients')}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{
                          color: theme.palette.text.secondary, lineHeight: 1.6,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' }
                        }}>
                          {selectedProduct.Ingredients}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                        {t('customProducts.startingFrom', 'From')}:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.primary.main, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                        {getPriceInCurrency(selectedProduct, selectedCurrency).toFixed(decimals)} {currencySymbol}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: { xs: 2.5, sm: 3 } }} />
                  </Box>
                );
              })()}

              {/* Subproducts */}
              {selectedProduct.SubProducts && selectedProduct.SubProducts.length > 0 ? (
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} justifyContent="center">
                  {selectedSubProducts.map((sp, idx) => {
                    const spImageUrl = getImageUrl(sp.ImageUrl);
                    const spPrice = getPriceInCurrency(sp, selectedCurrency);
                    
                    return (
                      <Grid item xs={6} sm={6} md={4} lg={3} key={sp.Id || idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Grow in timeout={300 + idx * 50}>
                          <Card sx={{ 
                            width: '100%', maxWidth: { xs: 170, sm: 200, md: 240 }, minHeight: { xs: 280, sm: 300, md: 320 }, 
                            borderRadius: { xs: '16px', sm: '18px', md: '20px' }, 
                            boxShadow: sp.selected ? `0 ${isMobile ? '8px 20px' : '12px 32px'} ${alpha(theme.palette.primary.main, 0.25)}` : `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.common.black, 0.08)}`, 
                            border: sp.selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.2)}`, 
                            bgcolor: 'background.paper', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', overflow: 'visible', 
                            '&:hover': { boxShadow: `0 ${isMobile ? '10px 24px' : '16px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`, transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)', borderColor: theme.palette.primary.main, '& .subproduct-image': { transform: 'scale(1.1)' }, '& .sub-hover-overlay': { opacity: isMobile ? 0 : 1 } },
                            '&:active': { transform: 'scale(0.95)' }
                          }}>
                            <CardActionArea onClick={() => !sp.selected && handleSubProductToggle(idx)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', p: 0 }}>
                              {sp.ImageUrl && (
                                <Box sx={{ position: 'relative', width: '100%', height: { xs: 80, sm: 90, md: 100 }, overflow: 'hidden', bgcolor: alpha(theme.palette.primary.main, 0.03), cursor: 'zoom-in' }} onClick={(e) => handleImageClick(sp.ImageUrl, e)}>
                                  <CardMedia component="img" image={spImageUrl} alt={sp.Name} className="subproduct-image" sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px 20px 0 0', transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                                  {!isMobile && (
                                    <Box className="sub-hover-overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.6)}, transparent)`, opacity: 0, transition: 'opacity 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Click to Zoom</Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.8, sm: 1 }, flexGrow: 1, width: '100%' }}>
                                <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 700, textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' }, 
                                  color: sp.selected ? theme.palette.primary.main : theme.palette.text.primary, lineHeight: 1.3, 
                                  minHeight: '2.6em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'                                 }}>
                                  {sp.Name}
                                </Typography>
                                
                                {spPrice > 0 && (
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' }, color: theme.palette.primary.main }}>
                                    {spPrice.toFixed(decimals)} {currencySymbol}
                                  </Typography>
                                )}
                                
                                {sp.Description && (
                                  <Typography variant="caption" sx={{ 
                                    color: theme.palette.text.secondary, textAlign: 'center', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }, lineHeight: 1.4,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                  }}>
                                    {sp.Description}
                                  </Typography>
                                )}
                                
                                {sp.SizeType && (
                                  <Chip 
                                    label={`${t('sizeType', 'Size')}: ${sp.SizeType}`} size="small" 
                                    sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, height: { xs: 20, sm: 22, md: 24 } }} 
                                  />
                                )}

                                {(sp.showHeightInitial || sp.showWidthInitial) && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.8, sm: 1 }, width: '100%', mt: { xs: 0.5, sm: 1 } }} onClick={(e) => e.stopPropagation()}>
                                    {sp.showHeightInitial && (
                                      <TextField 
                                        label={t('height', 'Height')} type="text" size="small" value={sp.Height} 
                                        onChange={e => handleSubProductFieldChange(idx, 'Height', e.target.value)} 
                                        disabled={!sp.selected} fullWidth 
                                        sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, textAlign: 'center', py: { xs: 0.8, sm: 1 } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.85rem' } } }} 
                                      />
                                    )}
                                    {sp.showWidthInitial && (
                                      <TextField 
                                        label={t('width', 'Width')} type="text" size="small" value={sp.Width} 
                                        onChange={e => handleSubProductFieldChange(idx, 'Width', e.target.value)} 
                                        disabled={!sp.selected} fullWidth 
                                        sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, textAlign: 'center', py: { xs: 0.8, sm: 1 } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.85rem' } } }} 
                                      />
                                    )}
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1 }, mt: 'auto', pt: { xs: 1.5, sm: 2 }, width: '100%', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                  {!sp.selected ? (
                                    <Button variant="contained" size="small" fullWidth onClick={() => handleSubProductToggle(idx)} 
                                      sx={{ borderRadius: { xs: '10px', sm: '12px' }, fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, py: { xs: 0.8, sm: 1 }, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark, transform: 'scale(1.02)' }, '&:active': { transform: 'scale(0.98)' }, transition: 'all 0.2s ease' }}
                                    >
                                      {t('addToCart', 'Add')}
                                    </Button>
                                  ) : sp.FixedQuantity ? (
                                    <Chip label={t('fixedQuantity', 'Fixed Quantity')} sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }, height: { xs: 22, sm: 24, md: 26 } }} />
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.8, md: 1 }, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: { xs: '10px', sm: '12px' }, p: { xs: 0.4, sm: 0.5 }, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                                      <IconButton size="small" onClick={() => handleDecrement(idx)} disabled={Number(sp.Quantity || 0) <= 0} 
                                        sx={{ bgcolor: theme.palette.error.main, color: 'white', width: { xs: 24, sm: 26, md: 28 }, height: { xs: 24, sm: 26, md: 28 }, '&:hover': { bgcolor: theme.palette.error.dark, transform: 'scale(1.1)' }, '&:disabled': { bgcolor: alpha(theme.palette.error.main, 0.3) }, '&:active': { transform: 'scale(0.9)' }, transition: 'all 0.2s ease' }}
                                      >
                                        <RemoveIcon sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }} />
                                      </IconButton>
                                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' }, minWidth: { xs: '26px', sm: '28px', md: '32px' }, textAlign: 'center', color: theme.palette.primary.main }}>
                                        {sp.Quantity}
                                      </Typography>
                                      <IconButton size="small" onClick={() => handleIncrement(idx)} 
                                        sx={{ bgcolor: theme.palette.success.main, color: 'white', width: { xs: 24, sm: 26, md: 28 }, height: { xs: 24, sm: 26, md: 28 }, '&:hover': { bgcolor: theme.palette.success.dark, transform: 'scale(1.1)' }, '&:active': { transform: 'scale(0.9)' }, transition: 'all 0.2s ease' }}
                                      >
                                        <AddIcon sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }} />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grow>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 7, md: 8 }, px: 2 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
                    {t('noSubproducts', 'No subproducts available.')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 2, sm: 2.2, md: 2.5 }, 
          borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02), 
          flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, alignItems: 'stretch'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1 }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
              {t('total', 'Total')}:
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
              {Number(customTotal || 0).toFixed(decimals)} {currencySymbol}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: { xs: 1.2, sm: 1.5 }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } }}>
            <Button onClick={handleCloseCustomize} variant="outlined" size={isMobile ? "medium" : "large"}
              sx={{ 
                flex: { xs: 1, sm: '0 0 auto' }, borderRadius: { xs: '10px', sm: '12px' }, fontWeight: 700, 
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }, px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 1, sm: 1.1, md: 1.2 }, 
                borderColor: theme.palette.primary.main, color: theme.palette.primary.main, 
                '&:hover': { borderColor: theme.palette.primary.dark, bgcolor: alpha(theme.palette.primary.main, 0.05) },
                '&:active': { transform: 'scale(0.98)' }
              }}
            >
              {t('cancel', 'Cancel')}
            </Button>
            {/* Action Button in Modal: تسوق الان opening https://www.elorayem.com/product/{PRODUCT ID} in new tab */}
            <Button onClick={(e) => handleOpenExternalProduct(selectedProduct, e)} variant="contained" 
              size={isMobile ? "medium" : "large"}
              sx={{ 
                flex: { xs: 1, sm: '0 0 auto' }, borderRadius: { xs: '10px', sm: '12px' }, fontWeight: 700, 
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }, px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 1, sm: 1.1, md: 1.2 }, 
                bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, 
                boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`, 
                '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`, transform: isMobile ? 'none' : 'translateY(-2px)' }, 
                '&:active': { transform: 'scale(0.98)' },
                transition: 'all 0.3s ease', gap: { xs: 0.5, sm: 0.8, md: 1 } 
              }}
            >
              <OpenInNewIcon sx={{ fontSize: { xs: 18, sm: 19, md: 20 } }} />
              تسوق الان
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Image Zoom Modal */}
      <Modal open={!!selectedImage} onClose={handleCloseModal} closeAfterTransition 
        BackdropComponent={Backdrop} BackdropProps={{ timeout: 500, sx: { backdropFilter: 'blur(8px)', backgroundColor: alpha(theme.palette.common.black, 0.8) } }}
      >
        <Fade in={!!selectedImage}>
          <Box sx={{ 
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
            maxWidth: { xs: '95vw', sm: '90vw' }, maxHeight: { xs: '95vh', sm: '90vh' }, outline: 'none', 
            borderRadius: { xs: '16px', sm: '20px', md: '24px' }, overflow: 'hidden', 
            boxShadow: `0 ${isMobile ? '16px 32px' : '24px 48px'} ${alpha(theme.palette.common.black, 0.4)}`, 
            animation: 'modalZoomIn 0.3s ease-out', 
            '@keyframes modalZoomIn': { from: { transform: 'translate(-50%, -50%) scale(0.8)', opacity: 0 }, to: { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 } } 
          }}>
            <IconButton onClick={handleCloseModal} size={isMobile ? "small" : "medium"}
              sx={{ 
                position: 'absolute', top: { xs: 12, sm: 14, md: 16 }, right: { xs: 12, sm: 14, md: 16 }, 
                bgcolor: alpha(theme.palette.common.white, 0.95), backdropFilter: 'blur(10px)', zIndex: 1, 
                width: { xs: 36, sm: 40, md: 44 }, height: { xs: 36, sm: 40, md: 44 },
                boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.common.black, 0.2)}`, 
                '&:hover': { bgcolor: theme.palette.common.white, transform: 'rotate(90deg)' },
                '&:active': { transform: 'scale(0.9)' }, transition: 'all 0.3s ease' 
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
            </IconButton>
            <Box component="img" src={selectedImage} alt="Product" 
              sx={{ display: 'block', maxWidth: '100%', maxHeight: { xs: '95vh', sm: '90vh' }, objectFit: 'contain', borderRadius: { xs: '16px', sm: '20px', md: '24px' } }} 
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
